import { useMemo, useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  List, Broadcast, Bell, MagnifyingGlass, FadersHorizontal, ListChecks, Package,
  UserCheck, Lightning, Flag, WarningOctagon, Camera, FilePdf, Repeat, Robot,
  Plus, X, DotsThreeVertical, Fire, MapPin, Sparkle, UploadSimple, Copy, Warning,
  Notepad, User as UserIcon, CellSignalHigh as Signal,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import "./tactical.css";

// --- Small building blocks -------------------------------------------------
const StatChip = ({ icon: Icon, text, tone = "slate" }) => {
  const toneMap = {
    slate: "text-slate-300 border-slate-600/60",
    emerald: "text-emerald-300 border-emerald-500/40",
    cyan: "text-cyan-300 border-cyan-500/40",
    amber: "text-amber-300 border-amber-500/40",
    red: "text-red-300 border-red-500/40",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-[2px] rounded text-[9px] font-black uppercase tracking-[0.08em] bg-slate-900/60 border ${toneMap[tone]}`}>
      {Icon && <Icon size={9} weight="bold" />} {text}
    </span>
  );
};

const StatusTab = ({ active, icon: Icon, label, count, onClick, tone = "slate", testid }) => {
  const activeCls = active
    ? "bg-cyan-500 text-slate-950 shadow-lg shadow-cyan-500/30 border-cyan-400"
    : "bg-slate-900/60 border-slate-700 text-slate-200 hover:border-cyan-500/50";
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`h-8 px-3 rounded-lg text-[11px] font-black uppercase tracking-wider whitespace-nowrap inline-flex items-center gap-1.5 border ${activeCls}`}
    >
      {Icon && <Icon size={13} weight={active ? "fill" : "bold"} className={active ? "" : (tone === "cyan" ? "text-cyan-400" : tone === "emerald" ? "text-emerald-400" : "text-slate-400")} />}
      {label}
      {count != null && <span className={`font-mono ${active ? "text-slate-950/70" : "text-cyan-400"}`}>{count}</span>}
    </button>
  );
};

const QuickChip = ({ active, tone, icon: Icon, label, count, onClick, testid }) => {
  const tones = {
    red: active ? "bg-red-500 text-white border-red-500" : "bg-red-500/10 text-red-300 border-red-500/40",
    amber: active ? "bg-amber-500 text-slate-950 border-amber-500" : "bg-amber-500/10 text-amber-300 border-amber-500/40",
    sky: active ? "bg-sky-500 text-white border-sky-500" : "bg-sky-500/10 text-sky-300 border-sky-500/40",
    violet: active ? "bg-violet-500 text-white border-violet-500" : "bg-violet-500/10 text-violet-300 border-violet-500/40",
    emerald: active ? "bg-emerald-500 text-slate-950 border-emerald-500" : "bg-emerald-500/10 text-emerald-300 border-emerald-500/40",
  };
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.05em] border ${tones[tone]}`}
    >
      {Icon && <Icon size={11} weight="bold" />} {label}
      {count != null && <span className="font-mono opacity-80">· {count}</span>}
    </button>
  );
};

// --- Main toolbar ----------------------------------------------------------
export default function AdminJobsToolbar({
  jobs = [],
  statusFilter,
  legacyFilter,
  STATUS_LABEL,
  query,
  setQuery,
  isPM,
  setSearchParams,
  onNewJob,
  onImportCsv,
  onBulkImport,
  onAiClassify,
  onFixMap,
}) {
  const [actionsOpen, setActionsOpen] = useState(false);
  const { user } = useAuth();

  // --- Live radar sweep — detect new WOs since last poll -------------------
  const prevCountRef = useRef(null);
  const [newSince, setNewSince] = useState(0);   // count of WOs added in last poll
  const [sweepKey, setSweepKey] = useState(0);   // increments to retrigger animation
  useEffect(() => {
    const len = jobs.length;
    if (prevCountRef.current == null) {
      prevCountRef.current = len;
      return;
    }
    const prev = prevCountRef.current;
    prevCountRef.current = len;
    // Skip the initial data-load transition (0 -> N). Only fire when the list
    // has already been populated and a genuine new WO shows up.
    if (prev === 0) return;
    const delta = len - prev;
    if (delta > 0) {
      setNewSince(delta);
      setSweepKey(k => k + 1);
      const t = setTimeout(() => setNewSince(0), 6000);
      return () => clearTimeout(t);
    }
  }, [jobs.length]);

  // --- Derived counts (safe fallbacks) -------------------------------------
  const counts = useMemo(() => {
    const alive = jobs.filter(j => !["merged", "canceled", "completed"].includes(j.status));
    return {
      all: jobs.length,
      pool: jobs.filter(j => j.status === "pool").length,
      assigned: jobs.filter(j => j.status === "assigned").length,
      active: jobs.filter(j => j.status === "in_progress").length,
      completed: jobs.filter(j => j.status === "completed").length,
      needs_docs: jobs.filter(j => j.needs_docs && !j.docs_completed_at).length,
      habitability: jobs.filter(j => j.habitability && j.status !== "completed" && j.status !== "canceled" && j.status !== "merged").length,
      life_safety: jobs.filter(j => j.life_safety && j.status !== "completed" && j.status !== "canceled" && j.status !== "merged").length,
      recurring: alive.filter(j => j.recurring || j.parent_recurring_id).length,
    };
  }, [jobs]);

  const setStatus = (val) => {
    if (!val) {
      setSearchParams({});
    } else {
      setSearchParams({ status: val });
    }
  };

  const tabs = [
    { key: "",           label: "All",       icon: ListChecks, count: counts.all,       testid: "tab-all" },
    { key: "pool",       label: "Pool",      icon: Package,    count: counts.pool,      testid: "tab-pool" },
    { key: "assigned",   label: "Assigned",  icon: UserCheck,  count: counts.assigned,  testid: "tab-assigned" },
    { key: "in_progress",label: "Active",    icon: Lightning,  count: counts.active,    testid: "tab-active", tone: "cyan" },
    { key: "completed",  label: "Done",      icon: Flag,       count: counts.completed, testid: "tab-completed", tone: "emerald" },
  ];

  const title = legacyFilter
    ? `Legacy Jobs${statusFilter ? ` · ${STATUS_LABEL[statusFilter] || statusFilter}` : ""}`
    : statusFilter ? `Jobs · ${STATUS_LABEL[statusFilter] || statusFilter}` : "All Jobs";

  return (
    <div className="admin-jobs-toolbar -mx-4 sm:-mx-8 -mt-4 sm:-mt-8 mb-4">
      {/* ============== TACTICAL HEADER ============== */}
      <header className="tactical-header radar-bg text-white px-3 pt-2 pb-2 border-b border-cyan-500/20 relative overflow-hidden">
        {/* Live radar sweep — retriggers when new WOs arrive */}
        {sweepKey > 0 && (
          <div key={sweepKey} className="radar-sweep-overlay" aria-hidden="true"/>
        )}
        {/* Micro status strip */}
        <div className="flex items-center gap-1.5 mb-1.5 relative z-10">
          <StatChip icon={() => <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 blink inline-block"/>} text="Online" tone="emerald" />
          <StatChip icon={Signal} text={`${jobs.length} loaded`} tone="cyan" />
          {counts.habitability > 0 && (
            <StatChip icon={WarningOctagon} text={`${counts.habitability} Hab`} tone="red" />
          )}
          {newSince > 0 && (
            <span className="incoming-chip inline-flex items-center gap-1 px-2 py-[2px] rounded-full text-[10px] font-black uppercase tracking-widest bg-cyan-500 text-slate-950 shadow-lg shadow-cyan-400/60" data-testid="incoming-new-chip">
              <Broadcast size={11} weight="fill"/> +{newSince} New
            </span>
          )}
          <span className="ml-auto text-[9px] text-slate-400 font-mono uppercase tracking-widest hidden xs:inline">
            {title}
          </span>
        </div>

        <div className="flex items-center gap-2 relative z-10">
          <div className={`h-10 w-10 rounded-lg bg-gradient-to-br from-cyan-400 to-cyan-700 flex items-center justify-center shadow-lg shadow-cyan-500/30 relative shrink-0 ${newSince > 0 ? "dish-active" : ""}`}>
            <Broadcast size={20} weight="fill" className="text-white"/>
            <span className="absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-full bg-emerald-400 border-2 border-slate-950 blink"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-black uppercase tracking-[0.22em] text-white">Field<span className="text-cyan-400">Radar</span></span>
              <span className="mono text-[9px] text-slate-500 hidden sm:inline">· Ops Console</span>
            </div>
            <div className="text-[11px] text-slate-400 truncate flex items-center gap-1">
              <UserIcon size={10} weight="fill" className="text-cyan-500"/>
              {user?.name || user?.email || "Admin"} · Live
            </div>
          </div>

          {/* Actions sheet trigger */}
          <Sheet open={actionsOpen} onOpenChange={setActionsOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="admin-jobs-actions-btn"
                className="relative h-10 w-10 rounded-lg bg-white/5 hover:bg-white/10 border border-cyan-500/25 flex items-center justify-center"
                title="Bulk actions & tools"
              >
                <DotsThreeVertical size={22} weight="bold" className="text-cyan-300"/>
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md p-0 bg-slate-950 border-l border-cyan-500/20 text-white">
              <SheetHeader className="px-4 py-3 border-b border-cyan-500/20 radar-bg">
                <SheetTitle className="text-cyan-300 uppercase tracking-widest text-sm font-black inline-flex items-center gap-2">
                  <FadersHorizontal size={16} weight="bold"/> Ops Toolkit
                </SheetTitle>
              </SheetHeader>
              <div className="p-4 space-y-4 max-h-[calc(100vh-64px)] overflow-y-auto">
                <ActionsGroup title="Import & AI">
                  <ActionButton icon={UploadSimple} label="Import CSV" tone="slate" onClick={() => { setActionsOpen(false); onImportCsv?.(); }} testid="tool-import-csv"/>
                  <ActionButton icon={Sparkle} label="AI Bulk Import" tone="cyan" onClick={() => { setActionsOpen(false); onBulkImport?.(); }} testid="tool-bulk-import"/>
                  <ActionButton icon={Robot} label="AI Auto-Tag Jobs" tone="violet" onClick={() => { setActionsOpen(false); onAiClassify?.(); }} testid="tool-ai-classify"/>
                  <ActionButton icon={MapPin} label="Re-geocode Map Pins" tone="fuchsia" onClick={() => { setActionsOpen(false); onFixMap?.(); }} testid="tool-fix-map"/>
                </ActionsGroup>

                <ActionsGroup title="Queues">
                  <ActionLinkButton to="/admin/needs-docs/batch" icon={Camera} label="Batch Photo Drop" tone="cyan" onClick={() => setActionsOpen(false)} testid="tool-batch-photos"/>
                  <ActionButton icon={Notepad} label={`Needs Docs · ${counts.needs_docs}`} tone="amber" onClick={() => { setActionsOpen(false); setSearchParams({ status: "needs_docs" }); }} testid="tool-needs-docs"/>
                  <ActionLinkButton to="/admin/duplicates" icon={Copy} label="Duplicate Queue" tone="fuchsia" onClick={() => setActionsOpen(false)} testid="tool-duplicates"/>
                </ActionsGroup>

                {isPM && (
                  <ActionsGroup title="Compliance">
                    <ActionButton icon={Warning} label={`Life Safety · ${counts.life_safety}`} tone="red" onClick={() => { setActionsOpen(false); setSearchParams({ status: "life_safety" }); }} testid="tool-life-safety"/>
                    <ActionButton icon={FilePdf} label={`Habitability · ${counts.habitability}`} tone="orange" onClick={() => { setActionsOpen(false); setSearchParams({ status: "habitability" }); }} testid="tool-habitability"/>
                    <ActionLinkButton to="/admin/safety-board" icon={Fire} label="Safety War-Room" tone="red" onClick={() => setActionsOpen(false)} testid="tool-safety-board"/>
                  </ActionsGroup>
                )}
              </div>
            </SheetContent>
          </Sheet>

          {/* New Job — always visible, glowing CTA */}
          <button
            onClick={onNewJob}
            data-testid="new-job-btn"
            className="h-10 pl-3 pr-4 rounded-lg bg-gradient-to-br from-cyan-400 via-cyan-500 to-cyan-700 text-slate-950 font-black text-[11px] uppercase tracking-wider shadow-lg shadow-cyan-500/40 inline-flex items-center gap-1.5 border border-cyan-300/40"
            title="New work order"
          >
            <Plus size={14} weight="bold"/> <span className="hidden xs:inline">New</span>
          </button>
        </div>

        {/* Search */}
        <div className="mt-2 relative">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-cyan-500 pointer-events-none"/>
          <input list="known-addresses" autoComplete="off"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search address, WO#, tech, tag…"
            data-testid="jobs-search-input"
            className="w-full bg-slate-950/70 border border-cyan-500/30 rounded-lg pl-8 pr-16 py-2 text-[13px] text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/40"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              data-testid="jobs-search-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10"
            >
              <X size={12} weight="bold"/>
            </button>
          )}
        </div>
      </header>

      {/* ============== STATUS TABS ============== */}
      <div className="bg-slate-950 border-b border-cyan-500/10 overflow-x-auto scrollbar-hide">
        <div className="flex gap-1.5 px-3 py-2 min-w-max">
          {tabs.map(t => (
            <StatusTab
              key={t.key || "all"}
              active={(statusFilter || "") === t.key}
              icon={t.icon}
              label={t.label}
              count={t.count}
              onClick={() => setStatus(t.key)}
              tone={t.tone}
              testid={t.testid}
            />
          ))}
        </div>
      </div>

      {/* ============== QUICK CHIPS ============== */}
      <div className="bg-[#070b14] border-b border-white/5 overflow-x-auto scrollbar-hide">
        <div className="flex gap-1.5 px-3 py-2 min-w-max">
          {counts.habitability > 0 && (
            <QuickChip
              tone="red"
              icon={WarningOctagon}
              label="Habitability"
              count={counts.habitability}
              active={statusFilter === "habitability"}
              onClick={() => setSearchParams(statusFilter === "habitability" ? {} : { status: "habitability" })}
              testid="chip-habitability"
            />
          )}
          {counts.needs_docs > 0 && (
            <QuickChip
              tone="amber"
              icon={Camera}
              label="Needs Docs"
              count={counts.needs_docs}
              active={statusFilter === "needs_docs"}
              onClick={() => setSearchParams(statusFilter === "needs_docs" ? {} : { status: "needs_docs" })}
              testid="chip-needs-docs"
            />
          )}
          {isPM && counts.life_safety > 0 && (
            <QuickChip
              tone="red"
              icon={Warning}
              label="Life Safety"
              count={counts.life_safety}
              active={statusFilter === "life_safety"}
              onClick={() => setSearchParams(statusFilter === "life_safety" ? {} : { status: "life_safety" })}
              testid="chip-life-safety"
            />
          )}
          {counts.recurring > 0 && (
            <QuickChip
              tone="violet"
              icon={Repeat}
              label="Recurring"
              count={counts.recurring}
              onClick={() => {}}
              testid="chip-recurring"
            />
          )}
          <Link to="/admin/needs-docs/batch" data-testid="chip-batch-photos">
            <QuickChip tone="sky" icon={Sparkle} label="Batch Photos" />
          </Link>
          <Link to="/admin/duplicates" data-testid="chip-duplicates">
            <QuickChip tone="emerald" icon={Copy} label="Dupes" />
          </Link>
        </div>
      </div>
    </div>
  );
}

// --- Actions Sheet building blocks ----------------------------------------
function ActionsGroup({ title, children }) {
  return (
    <div>
      <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mb-2 px-1">{title}</div>
      <div className="grid grid-cols-2 gap-2">{children}</div>
    </div>
  );
}

function ActionButton({ icon: Icon, label, tone = "slate", onClick, testid }) {
  const tones = {
    slate: "bg-slate-900 hover:bg-slate-800 border-slate-700 text-slate-100",
    cyan: "bg-cyan-500/10 hover:bg-cyan-500/20 border-cyan-500/40 text-cyan-200",
    violet: "bg-violet-500/10 hover:bg-violet-500/20 border-violet-500/40 text-violet-200",
    amber: "bg-amber-500/10 hover:bg-amber-500/20 border-amber-500/40 text-amber-200",
    red: "bg-red-500/10 hover:bg-red-500/20 border-red-500/40 text-red-200",
    orange: "bg-orange-500/10 hover:bg-orange-500/20 border-orange-500/40 text-orange-200",
    fuchsia: "bg-fuchsia-500/10 hover:bg-fuchsia-500/20 border-fuchsia-500/40 text-fuchsia-200",
  };
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex flex-col items-start gap-1.5 p-3 rounded-lg border ${tones[tone]} text-left transition`}
    >
      {Icon && <Icon size={18} weight="fill"/>}
      <span className="text-[11px] font-black uppercase tracking-wider leading-tight">{label}</span>
    </button>
  );
}

function ActionLinkButton({ to, icon: Icon, label, tone, onClick, testid }) {
  const tones = {
    slate: "bg-slate-900 hover:bg-slate-800 border-slate-700 text-slate-100",
    cyan: "bg-cyan-500/10 hover:bg-cyan-500/20 border-cyan-500/40 text-cyan-200",
    fuchsia: "bg-fuchsia-500/10 hover:bg-fuchsia-500/20 border-fuchsia-500/40 text-fuchsia-200",
    red: "bg-red-500/10 hover:bg-red-500/20 border-red-500/40 text-red-200",
    amber: "bg-amber-500/10 hover:bg-amber-500/20 border-amber-500/40 text-amber-200",
  };
  return (
    <Link
      to={to}
      data-testid={testid}
      onClick={onClick}
      className={`flex flex-col items-start gap-1.5 p-3 rounded-lg border ${tones[tone]} text-left transition`}
    >
      {Icon && <Icon size={18} weight="fill"/>}
      <span className="text-[11px] font-black uppercase tracking-wider leading-tight">{label}</span>
    </Link>
  );
}
